import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildShuffleOrder,
  getNextTrackIndex,
  playlistEqGainsFromSource,
  playlistHasNextTrack,
  storageBoxItemFromTrack,
  trackFromStorageBoxItem,
} from './audioPlaylistStorage';

export function useAudioPlaylist(
  initialItem,
  {
    enabled = true,
    makeTrack = trackFromStorageBoxItem,
    initialTracks = null,
    initialMeta = null,
  } = {}
) {
  const hasInitialPlaylist = Boolean(initialTracks?.length);
  const [playlistName, setPlaylistName] = useState(() =>
    initialMeta?.name ? String(initialMeta.name) : 'My playlist'
  );
  const [tracks, setTracks] = useState(() => (hasInitialPlaylist ? initialTracks : []));
  const [currentTrackId, setCurrentTrackId] = useState(() =>
    hasInitialPlaylist ? initialTracks[0]?.trackId || '' : ''
  );
  const [shuffle, setShuffle] = useState(() => Boolean(initialMeta?.shuffle));
  const [repeat, setRepeat] = useState(() => Boolean(initialMeta?.repeat));
  const [eqGains, setEqGains] = useState(() => playlistEqGainsFromSource(initialMeta));
  const [playOrder, setPlayOrder] = useState([]);
  const [status, setStatus] = useState('');
  const [autoPlayRequest, setAutoPlayRequest] = useState(0);

  const requestAutoPlay = useCallback(() => {
    setAutoPlayRequest((n) => n + 1);
  }, []);

  useEffect(() => {
    if (hasInitialPlaylist) return;
    if (!enabled || !initialItem?.itemId) return;
    const track = makeTrack(initialItem);
    setTracks((prev) => {
      const existing = prev.find((t) => t.source === 'storagebox' && t.itemId === track.itemId);
      if (existing) return prev;
      if (!prev.length) {
        setCurrentTrackId(track.trackId);
        queueMicrotask(() => requestAutoPlay());
        return [track];
      }
      return [...prev, track];
    });
  }, [enabled, hasInitialPlaylist, initialItem?.itemId, initialItem, makeTrack, requestAutoPlay]);

  const currentIndex = useMemo(
    () => tracks.findIndex((t) => t.trackId === currentTrackId),
    [tracks, currentTrackId]
  );

  useEffect(() => {
    if (!tracks.length) {
      setPlayOrder([]);
      return;
    }
    const start = currentIndex >= 0 ? currentIndex : 0;
    setPlayOrder(shuffle ? buildShuffleOrder(tracks.length, start) : tracks.map((_, i) => i));
  }, [tracks.length, shuffle]); // eslint-disable-line react-hooks/exhaustive-deps

  const addTrackFromItem = useCallback(
    (item) => {
      const track = makeTrack(item);
      setTracks((prev) => {
        const exists = prev.some((t) => t.source === 'storagebox' && t.itemId === track.itemId);
        if (exists) {
          const existing = prev.find((t) => t.source === 'storagebox' && t.itemId === track.itemId);
          if (existing) setCurrentTrackId(existing.trackId);
          setStatus('Track is already in the playlist.');
          return prev;
        }
        setStatus('Added to playlist.');
        setCurrentTrackId(track.trackId);
        return [...prev, track];
      });
    },
    [makeTrack]
  );

  const playItemNow = useCallback(
    (item) => {
      if (!item?.itemId) return;
      const track = makeTrack(item);
      setTracks((prev) => {
        const existing = prev.find((t) => t.source === 'storagebox' && t.itemId === track.itemId);
        if (existing) {
          setCurrentTrackId(existing.trackId);
          setStatus('Now playing.');
          requestAutoPlay();
          return prev;
        }
        setCurrentTrackId(track.trackId);
        setStatus('Now playing.');
        requestAutoPlay();
        return prev.length ? [...prev, track] : [track];
      });
    },
    [makeTrack, requestAutoPlay]
  );

  const selectTrack = useCallback(
    (trackId) => {
      if (!trackId) return;
      setCurrentTrackId(trackId);
      setStatus('Now playing.');
      requestAutoPlay();
    },
    [requestAutoPlay]
  );

  /** Insert after the current track without changing what is playing now. */
  const queueTrackPlayNext = useCallback(
    (item) => {
      if (!item?.itemId) return;
      const track = makeTrack(item);
      setTracks((prev) => {
        if (!prev.length) {
          setCurrentTrackId(track.trackId);
          setStatus('Now playing.');
          requestAutoPlay();
          return [track];
        }
        let list = [...prev];
        const existingIdx = list.findIndex(
          (t) => t.source === 'storagebox' && t.itemId === track.itemId
        );
        let entry = track;
        if (existingIdx >= 0) {
          entry = list[existingIdx];
          if (entry.trackId === currentTrackId) {
            setStatus('This track is playing now.');
            return prev;
          }
          list.splice(existingIdx, 1);
        }
        const curIdx = list.findIndex((t) => t.trackId === currentTrackId);
        const insertAt = curIdx >= 0 ? curIdx + 1 : list.length;
        list.splice(insertAt, 0, entry);
        setStatus('Queued to play next.');
        return list;
      });
    },
    [makeTrack, currentTrackId, requestAutoPlay]
  );

  const addCurrentTrack = useCallback(() => {
    if (!initialItem?.itemId) {
      setStatus('Nothing to add.');
      return;
    }
    addTrackFromItem(initialItem);
  }, [initialItem, addTrackFromItem]);

  const advanceTrack = useCallback(() => {
    if (tracks.length <= 1) return;
    const nextIndex = getNextTrackIndex(tracks.length, currentIndex, playOrder, repeat);
    if (nextIndex == null) return;
    setCurrentTrackId(tracks[nextIndex]?.trackId || '');
    requestAutoPlay();
  }, [tracks, playOrder, currentIndex, repeat, requestAutoPlay]);

  const loadTracks = useCallback(
    (loaded, meta) => {
      setTracks(loaded || []);
      setCurrentTrackId(loaded?.[0]?.trackId || '');
      if (meta?.name) setPlaylistName(meta.name);
      if (typeof meta?.shuffle === 'boolean') setShuffle(meta.shuffle);
      if (typeof meta?.repeat === 'boolean') setRepeat(meta.repeat);
      setEqGains(playlistEqGainsFromSource(meta));
      if (loaded?.length) requestAutoPlay();
    },
    [requestAutoPlay]
  );

  const hasNextTrackInQueue = useMemo(
    () => playlistHasNextTrack(tracks.length, currentIndex, playOrder, repeat),
    [tracks.length, currentIndex, playOrder, repeat]
  );

  const currentStorageItem = useMemo(() => {
    const track = tracks.find((t) => t.trackId === currentTrackId);
    return storageBoxItemFromTrack(track) || initialItem;
  }, [tracks, currentTrackId, initialItem]);

  return {
    playlistName,
    setPlaylistName,
    tracks,
    setTracks,
    currentTrackId,
    setCurrentTrackId,
    shuffle,
    setShuffle,
    repeat,
    setRepeat,
    eqGains,
    setEqGains,
    status,
    setStatus,
    addTrackFromItem,
    playItemNow,
    queueTrackPlayNext,
    addCurrentTrack,
    advanceTrack,
    loadTracks,
    currentStorageItem,
    hasNextTrackInQueue,
    autoPlayRequest,
    selectTrack,
  };
}
